import React, { Fragment, useEffect, useState, Suspense } from "react";
import ReactDOM from "react-dom/client";
import { HashRouter, Route, Routes } from "react-router-dom";
import "./service/apiConfig";
import "./index.scss";
import "./tailwind.css";
import "./layouts/chrome-polish.css";
import "./components/helper/PageTrail.css";
import "./components/helper/DocumentTotalsPill.css";
import "./components/helper/PageStickyActionBar.css";
import "./components/Masters/Masters.css";
import Loader from "./layouts/Loader/Loader";
import Auth from "./Authentication/auth";
import { AuthService, VENDOR_PORTAL_LOGOUT_EVENT } from "./service/AuthService";
import { MODULES } from "./constants/constants";
import { isSuperAdminRole } from "./components/helper/utility";
import { Button } from "react-bootstrap";
import { capturePostLoginRedirectFromHash } from "./utils/postLoginRedirect";
import ScrollToTop from "./components/helper/ScrollToTop";

// Lazy-loaded components
const App = React.lazy(() => import("./components/app"));
const AuthLogin = React.lazy(() => import("./Authentication/Login"));
const Forgotpassword = React.lazy(() => import("./components/Custompages/Forgotpassword/Forgotpassword"));
const VendorDashboard = React.lazy(() => import("./components/Dashboard/VendorDashboard"));
const Masters = React.lazy(() => import("./components/Masters/Masters"));
const Vendors = React.lazy(() => import("./components/Vendors/Vendors"));
const AddVendor = React.lazy(() => import("./components/Vendors/AddVendor"));
const Roles = React.lazy(() => import("./components/Roles/Roles"));
const PermissionsPage = React.lazy(() => import("./components/Permissions/PermissionsPage"));
const MDMReports = React.lazy(() => import("./components/MDMReports/MDMReports"));
const ListPRs = React.lazy(() => import("./components/PurchaseRequisitions/ListPRs"));
const AddEditPr = React.lazy(() => import("./components/PurchaseRequisitions/AddEditPr"));
const ViewPr = React.lazy(() => import("./components/PurchaseRequisitions/ViewPr"));
const ListRfqs = React.lazy(() => import("./components/RequestForQuotations/ListRfqs"));
const AddEditRfq = React.lazy(() => import("./components/RequestForQuotations/AddEditRfq"));
const ViewRfq = React.lazy(() => import("./components/RequestForQuotations/ViewRfq"));
const ListVendorQuotations = React.lazy(() => import("./components/VendorQuotations/ListVendorQuotations"));
const AddEditVendorQuotation = React.lazy(() => import("./components/VendorQuotations/AddEditVendorQuotation"));
const ViewVendorQuotation = React.lazy(() => import("./components/VendorQuotations/ViewVendorQuotation"));
const Modules = React.lazy(() => import("./components/Modules/Modules"));
const Profile = React.lazy(() => import("./components/Pages/Profile/Profile"));
const Users = React.lazy(() => import("./components/Users/Users"));
const ListPOs = React.lazy(() => import("./components/PurchaseOrders/ListPOs"));
const AddEditPo = React.lazy(() => import("./components/PurchaseOrders/AddEditPo"));
const ViewPo = React.lazy(() => import("./components/PurchaseOrders/ViewPo"));
const SubDepartments = React.lazy(() => import("./components/Masters/SubDepartments"));
const Departments = React.lazy(() => import("./components/Masters/Departments"));
const RoleCountryDepartmentMapping = React.lazy(() => import("./components/RoleCountryDepartmentMapping/RoleCountryDepartmentMapping"));
const ApprovalThreshold = React.lazy(() => import("./components/ApprovalThreshold/ApprovalThreshold"));
const ListGrns = React.lazy(() => import("./components/Shipments/ListGrns"));
const ListServiceDeliveries = React.lazy(
  () => import("./components/ServiceDelivery/ListServiceDeliveries")
);
const InvoiceParking = React.lazy(() => import("./components/InvoiceParking/InvoiceParking"));
const AddEditGrn = React.lazy(() => import("./components/Shipments/AddEditGrn"));
const AddEditServiceDelivery = React.lazy(
  () => import("./components/ServiceDelivery/AddEditServiceDelivery")
);
const ViewGrn = React.lazy(() => import("./components/Shipments/ViewGrn"));
const ViewServiceDelivery = React.lazy(
  () => import("./components/ServiceDelivery/ViewServiceDelivery")
);
const Invoices = React.lazy(() => import("./components/Invoices/Invoices"));
const POPaymentSummary = React.lazy(() =>
  import("./components/POPaymentSummary/POPaymentSummary")
);
const UserFormPage = React.lazy(() => import("./components/Users/UserFormPage"));
const ViewUser = React.lazy(() => import("./components/Users/ViewUser"));

const NotFound = () => {
  return (
    <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 p-4" style={{ backgroundColor: '#f8f9fa' }}>
      <div className="text-center">
        <div style={{ fontSize: '120px', fontWeight: 'bold', color: '#228be6', marginBottom: '20px' }}>
          404
        </div>
        <h2 className="mb-3" style={{ color: '#495057' }}>Page Not Found</h2>
        <p className="mb-4" style={{ color: '#868e96', maxWidth: '500px' }}>
          The page you are looking for either doesn't exist or you do not have access to it.
        </p>
        <Button 
          variant="primary" 
          onClick={() => window.history.back()}
          style={{ 
            padding: '10px 24px',
            fontSize: '16px',
            borderRadius: '4px'
          }}
        >
          Go Back
        </Button>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ element, requiredModule, authOnly = false }) => {
  const user = AuthService.currentUser();
  if (!user) {
    return <Loader />;
  }
  if (authOnly) {
    return element;
  }
  const permissions = user?.permissions || {};

  const hasAccess =
    isSuperAdminRole(user) ||
    permissions[requiredModule]?.includes("MENU");

  return hasAccess ? element : <NotFound />
};

const Root = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const currentUser = AuthService.currentUser();
    if (currentUser) {
      // Re-load user from listUsers by id (not stale login payload), then permissions
      AuthService.refreshSessionUser()
        .then(() => {
          setIsAuthenticated(true);
          setIsLoading(false);
        })
        .catch(() => {
          // fallback if profile / permission fetch fails — keep existing session
          setIsAuthenticated(true);
          setIsLoading(false);
        });
    } else {
      // Preserve email/deep-link hash (e.g. #/add-pr/123?mode=view) for post-login redirect
      capturePostLoginRedirectFromHash();
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  }, []);

  // Same-tab logout clears localStorage without a full document reload; keep router auth in sync.
  useEffect(() => {
    const onLogout = () => {
      setIsAuthenticated(false);
      setIsLoading(false);
    };
    window.addEventListener(VENDOR_PORTAL_LOGOUT_EVENT, onLogout);
    return () => window.removeEventListener(VENDOR_PORTAL_LOGOUT_EVENT, onLogout);
  }, []);

  if (isLoading) return <Loader />;

  return (
    <Fragment>
      <HashRouter>
        <ScrollToTop />
        <Suspense fallback={<Loader />}>
          <Routes>
            {!isAuthenticated ? (
              <Route path={`${process.env.PUBLIC_URL}/`} element={<Auth />}>
                <Route index element={<AuthLogin setIsLogin={setIsAuthenticated} />} />
                <Route path="authentication/login" element={<AuthLogin setIsLogin={setIsAuthenticated} />} />
                <Route path="auth/forgot-password" element={<Forgotpassword />} />
                <Route path="*" element={<AuthLogin setIsLogin={setIsAuthenticated} />} />
              </Route>
            ) : (
              <Route path={`${process.env.PUBLIC_URL}/`} element={<App />}>
                <Route index element={
                  <ProtectedRoute element={<VendorDashboard />} requiredModule={MODULES.DASHBOARD} />
                } />
                <Route path="dashboard" element={
                  <ProtectedRoute element={<VendorDashboard />} requiredModule={MODULES.DASHBOARD} />
                } />
                <Route path="main-dashboard" element={
                  <ProtectedRoute element={<VendorDashboard />} requiredModule={MODULES.DASHBOARD} />
                } />
                <Route path="vendor-dashboard" element={
                  <ProtectedRoute element={<VendorDashboard />} requiredModule={MODULES.DASHBOARD} />
                } />
                <Route path="masters" element={
                  <ProtectedRoute element={<Masters />} requiredModule={MODULES.MASTERS} />
                } />
                <Route path="purchase-requisitions" element={
                  <ProtectedRoute element={<ListPRs />} requiredModule={MODULES.PR} />
                } />
                <Route path="purchase-requisitions/new" element={
                  <ProtectedRoute element={<AddEditPr />} requiredModule={MODULES.PR} />
                } />
                <Route path="purchase-requisitions/:id/edit" element={
                  <ProtectedRoute element={<AddEditPr />} requiredModule={MODULES.PR} />
                } />
                <Route path="purchase-requisitions/:id" element={
                  <ProtectedRoute element={<ViewPr />} requiredModule={MODULES.PR} />
                } />
                <Route path="request-for-quotations" element={
                  <ProtectedRoute element={<ListRfqs />} requiredModule={MODULES.RFQ} />
                } />
                <Route path="request-for-quotations/new" element={
                  <ProtectedRoute element={<AddEditRfq />} requiredModule={MODULES.RFQ} />
                } />
                <Route path="request-for-quotations/:id/edit" element={
                  <ProtectedRoute element={<AddEditRfq />} requiredModule={MODULES.RFQ} />
                } />
                <Route path="request-for-quotations/:id" element={
                  <ProtectedRoute element={<ViewRfq />} requiredModule={MODULES.RFQ} />
                } />
                <Route path="vendor-quotations" element={
                  <ProtectedRoute element={<ListVendorQuotations />} requiredModule={MODULES.QUOTATIONS} />
                } />
                <Route path="vendor-quotations/:id/edit" element={
                  <ProtectedRoute element={<AddEditVendorQuotation />} requiredModule={MODULES.QUOTATIONS} />
                } />
                <Route path="vendor-quotations/:id" element={
                  <ProtectedRoute element={<ViewVendorQuotation />} requiredModule={MODULES.QUOTATIONS} />
                } />
                <Route path="vendors" element={
                  <ProtectedRoute element={<Vendors />} requiredModule={MODULES.VENDORS} />
                } />
                <Route path="add-vendor" element={
                  <ProtectedRoute element={<AddVendor />} requiredModule={MODULES.VENDORS} />
                } />
                <Route path="pages/profile" element={
                  <ProtectedRoute element={<Profile />} authOnly />
                } />
                <Route path="roles" element={
                  <ProtectedRoute element={<Roles />} requiredModule={MODULES.ROLES} />
                } />
                <Route path="roles/:roleId/permissions" element={
                  <ProtectedRoute element={<PermissionsPage />} requiredModule={MODULES.ROLES} />
                } />
                <Route path="mdmreports" element={
                  <ProtectedRoute element={<MDMReports />} requiredModule={MODULES.MDMREPORTS} />
                } />
                <Route path="modules" element={
                  <ProtectedRoute element={<Modules />} requiredModule={MODULES.MODULES} />
                } />
                <Route path="users" element={
                  <ProtectedRoute element={<Users />} requiredModule={MODULES.USERS} />
                } />
                <Route path="users/create" element={
                  <ProtectedRoute element={<UserFormPage mode="create" />} requiredModule={MODULES.USERS} />
                } />
                <Route path="users/edit/:id" element={
                  <ProtectedRoute element={<UserFormPage mode="edit" />} requiredModule={MODULES.USERS} />
                } />
                <Route path="users/view/:id" element={
                  <ProtectedRoute element={<ViewUser />} requiredModule={MODULES.USERS} />
                } />
                <Route path="purchase-order" element={
                  <ProtectedRoute element={<ListPOs />} requiredModule={MODULES.PURCHASE_ORDERS} />
                } />
                <Route path="purchase-order/new" element={
                  <ProtectedRoute element={<AddEditPo />} requiredModule={MODULES.PURCHASE_ORDERS} />
                } />
                <Route path="purchase-order/:id/edit" element={
                  <ProtectedRoute element={<AddEditPo />} requiredModule={MODULES.PURCHASE_ORDERS} />
                } />
                <Route path="purchase-order/:id" element={
                  <ProtectedRoute element={<ViewPo />} requiredModule={MODULES.PURCHASE_ORDERS} />
                } />
                <Route path="subdepartments" element={
                  <ProtectedRoute element={<SubDepartments />} requiredModule={MODULES.MASTERS} />
                } />
                <Route path="departments" element={
                  <ProtectedRoute element={<Departments />} requiredModule={MODULES.MASTERS} />
                } />
                <Route path="role-country-mapping" element={
                  <ProtectedRoute element={<RoleCountryDepartmentMapping />} requiredModule={MODULES.ROLE_COUNTRY_MAPPING} />
                } />
                <Route path="approval-threshold" element={
                  <ProtectedRoute element={<ApprovalThreshold />} requiredModule={MODULES.APPROVAL_THRESHOLD} />
                } />
                <Route path="shipments" element={
                  <ProtectedRoute element={<ListGrns />} requiredModule={MODULES.SHIPMENTS} />
                } />
                <Route path="service-delivery" element={
                  <ProtectedRoute element={<ListServiceDeliveries />} requiredModule={MODULES.SERVICE_DELIVERY} />
                } />
                <Route path="invoice-parking" element={
                  <ProtectedRoute element={<InvoiceParking />} requiredModule={MODULES.INVOICE_PARKING} />
                } />
                <Route path="add-shipment" element={
                  <ProtectedRoute element={<AddEditGrn />} requiredModule={MODULES.SHIPMENTS} />
                } />
                <Route path="add-service-delivery" element={
                  <ProtectedRoute element={<AddEditServiceDelivery />} requiredModule={MODULES.SERVICE_DELIVERY} />
                } />
                <Route path="edit-shipment/:id" element={
                  <ProtectedRoute element={<AddEditGrn />} requiredModule={MODULES.SHIPMENTS} />
                } />
                <Route path="edit-service-delivery/:id" element={
                  <ProtectedRoute element={<AddEditServiceDelivery />} requiredModule={MODULES.SERVICE_DELIVERY} />
                } />
                <Route path="view-shipment/:id" element={
                  <ProtectedRoute element={<ViewGrn />} requiredModule={MODULES.SHIPMENTS} />
                } />
                <Route path="view-service-delivery/:id" element={
                  <ProtectedRoute element={<ViewServiceDelivery />} requiredModule={MODULES.SERVICE_DELIVERY} />
                } />
                <Route path="invoices" element={
                  <ProtectedRoute element={<Invoices />} requiredModule={MODULES.INVOICES} />
                } />
                <Route path="po-payment-summary" element={
                  <ProtectedRoute
                    element={<POPaymentSummary />}
                    requiredModule={MODULES.PO_PAYMENT_SUMMARY}
                  />
                } />
              </Route>
            )}
          </Routes>
        </Suspense>
      </HashRouter>
    </Fragment>
  );
};

ReactDOM.createRoot(document.getElementById("root")).render(<Root />);
